import { ref } from 'vue';
import { homepageConfigService } from '@/services/homepageConfigService';

// Module-level shared cache, mirroring useAcademicYears.js: every consumer (navbar,
// footer, HomePageRenderer, …) reads the same list and triggers at most one network
// request. `setDraftSections` exists only for the dashboard's live iframe preview
// (see PreviewHome.vue): the preview still fetches the real, saved config on mount
// (so it is never blank, even if the postMessage handshake with the builder is
// slow, blocked, or never arrives) — draft-update messages then overlay the
// teacher's in-progress edits on top of that real baseline via setDraftSections.
const sections = ref([]);
const loaded = ref(false);
let inflight = null;

export function useHomepageConfig() {
  async function fetchHomepageConfig() {
    if (loaded.value) return sections.value;
    if (inflight) return inflight;

    inflight = homepageConfigService
      .getAll()
      .then((rows) => {
        sections.value = rows;
        loaded.value = true;
        return rows;
      })
      .catch(() => {
        sections.value = [];
        return [];
      })
      .finally(() => {
        inflight = null;
      });

    return inflight;
  }

  function getSection(key) {
    return sections.value.find((s) => s.key === key) ?? null;
  }

  // Merges the section's stored settings over the given fallback defaults, so a
  // partially-configured (or missing) section never renders "undefined"/"null".
  function getSettings(key, fallback = {}) {
    return { ...fallback, ...(getSection(key)?.settings ?? {}) };
  }

  function activeSections() {
    return sections.value
      .filter((s) => s.is_active)
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  // Used only by the /__preview/home route's postMessage listener.
  function setDraftSections(rows) {
    sections.value = rows;
    loaded.value = true;
  }

  return {
    sections,
    loaded,
    fetchHomepageConfig,
    getSection,
    getSettings,
    activeSections,
    setDraftSections,
  };
}
